import React, { useEffect, useRef, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Locale } from 'date-fns'
import styles from './DateTimeInputComponent.module.css'
import ko from 'date-fns/locale/ko'

registerLocale("ko", ko as unknown as Locale)

const YEARS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i - 2)
const MONTHS = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ]

interface DateTimePickerProps {
    selectedDate: Date
    setSelectedDate: (date: Date) => void,
    disabled?: boolean
}
  
const DateTimePicker:React.FC<DateTimePickerProps> = ({selectedDate, setSelectedDate, disabled=false}) => {

    const datePickerRef = useRef<DatePicker>(null)

    interface CustomTimeInputProps {
        date: Date
        hour: string
        minute: string
        isAM: boolean
        updateDate: (newDate: Date) => void
        datePickerRef?: React.RefObject<DatePicker>
    }

    const CustomTimeInputContainer:React.FC<CustomTimeInputProps> = ({ date, hour, minute, isAM, updateDate, datePickerRef}) =>  {
        const [timeState, setTimeState] = useState({hour:hour, minute:minute, isAM:isAM})

        const handleClick = (newState:any) => { setTimeState({...timeState,isAM:newState}) }

        const closeDatePicker = () => {
            if (datePickerRef && datePickerRef.current) {
                updateDate(makeNewDate())
                datePickerRef.current.setOpen(false)
            }
        }
        const handleHourChange = (e:any) => {
            const newHourValue = e.target.value

            if (newHourValue === "") setTimeState({...timeState, hour: "0"})
            else if (Number(newHourValue) < 0) setTimeState({...timeState, hour: "0"})
            else if (Number(newHourValue) > 12) setTimeState({...timeState, hour: "12"})
            else setTimeState({...timeState, hour: newHourValue})
        }

        const handleMinuteChange = (e:any) => {
            const newMinuteValue = e.target.value
        
            if (newMinuteValue === "") setTimeState({...timeState, minute: "00"})
            else if (Number(newMinuteValue) < 0) setTimeState({...timeState, minute: "00"})
            else if (Number(newMinuteValue) > 59) setTimeState({...timeState, minute: "59"})
            else setTimeState({...timeState, minute: newMinuteValue})
        }


        const makeNewDate = () => {
            if(hour === timeState.hour && minute === timeState.minute && isAM === timeState.isAM) return date

            const hours = timeState.isAM ? (timeState.hour === '12' ? 0 : parseInt(timeState.hour)) 
                                        : (timeState.hour === '12' ? 12 : parseInt(timeState.hour) + 12)
            const minutes = parseInt(timeState.minute)
            const newDate = new Date(date)

            newDate.setHours(hours)
            newDate.setMinutes(minutes)

            return newDate
        }
        

        return (
            <div className={styles.timeInputContainer}>
                <input 
                    type="number"
                    className={styles.timeInput}
                    value={timeState.hour}
                    onChange={handleHourChange}
                    style={{width: "30px", margin:0}}/>
                <span className={styles.timeDivider}>:</span>
                <input 
                    type="number"
                    className={styles.timeInput}
                    value={timeState.minute}
                    onChange={handleMinuteChange}
                    style={{width: "30px", margin:0}}
                    />
                <div className={styles.switchbtn}>
                    <button
                        className={`${styles.timebtn} ${timeState.isAM ? styles.active : ''}`}
                        onClick={() => handleClick(true)}>AM</button>
                    <button
                        className={`${styles.timebtn} ${!timeState.isAM ? styles.active : ''}`}
                        onClick={() => handleClick(false)}>PM</button>
                </div>
                <button className={styles.settingbtn} onClick={closeDatePicker}>설정</button>
            </div>
        )
    }

    
  return (
        <DatePicker
        ref={datePickerRef}
        selected={selectedDate}
        onChange={(date:any) => setSelectedDate(date)}
        locale={"ko"}
        dateFormat="yyyy. MM. dd. aa h:mm"
        renderCustomHeader={({ date, /* changeYear, */ decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled,}) => (
            <div className={styles.customHeaderContainer}>
              <div>
                <span className={styles.year}>{date.getFullYear()}</span>
                <span className={styles.month}>{MONTHS[date.getMonth()]}</span>
              </div>
              <div>
                <button
                  type='button'
                  onClick={decreaseMonth}
                  className={styles.monthButton}
                  disabled={prevMonthButtonDisabled}>
                    <svg width="11" height="18" viewBox="0 0 11 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.240234 8.94922C0.240234 9.28125 0.367188 9.56445 0.630859 9.81836L8.24805 17.2695C8.45312 17.4844 8.72656 17.5918 9.03906 17.5918C9.67383 17.5918 10.1719 17.1035 10.1719 16.459C10.1719 16.1465 10.0449 15.8633 9.83008 15.6484L2.96484 8.94922L9.83008 2.25C10.0449 2.02539 10.1719 1.74219 10.1719 1.42969C10.1719 0.794922 9.67383 0.306641 9.03906 0.306641C8.72656 0.306641 8.45312 0.414062 8.24805 0.628906L0.630859 8.08008C0.367188 8.33398 0.25 8.61719 0.240234 8.94922Z" fill="#0999A3"/>
                    </svg>
                </button>
                <button
                  type='button'
                  onClick={increaseMonth}
                  className={styles.monthButton}
                  disabled={nextMonthButtonDisabled}>
                    <svg width="11" height="18" viewBox="0 0 11 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.75 8.94922C10.75 8.61719 10.623 8.33398 10.3691 8.08008L2.75195 0.628906C2.53711 0.414062 2.27344 0.306641 1.96094 0.306641C1.32617 0.306641 0.818359 0.794922 0.818359 1.42969C0.818359 1.74219 0.945312 2.02539 1.16016 2.25L8.03516 8.94922L1.16016 15.6484C0.955078 15.8633 0.818359 16.1465 0.818359 16.459C0.818359 17.1035 1.32617 17.5918 1.96094 17.5918C2.27344 17.5918 2.53711 17.4844 2.75195 17.2695L10.3691 9.81836C10.6328 9.56445 10.75 9.28125 10.75 8.94922Z" fill="#0999A3"/>
                    </svg>
                </button>
              </div>
            </div>
        )}
        shouldCloseOnSelect={false}
        showTimeInput
        
        customTimeInput={
            <CustomTimeInputContainer
              date={selectedDate}
              hour= {(Number(selectedDate.getHours().toString().padStart(2, '0')) % 12).toString()}
              minute={selectedDate.getMinutes().toString().padStart(2, '0')}
              isAM={selectedDate.getHours() < 12 ? true: false}
              updateDate={setSelectedDate}
              datePickerRef={datePickerRef}/>
        }
        disabled={disabled}
        />
    )

    
}

export default DateTimePicker
